import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';

interface UserProfileDropdownProps {
  onOpenSettings: () => void;
}

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const UserChip = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 50px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #e5e7eb;
    border-color: #d1d5db;
  }
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
`;

const UserInfo = styled.div`
  text-align: left;
`;

const UserName = styled.div`
  color: #1f2937;
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.2;
`;

const UserRole = styled.div`
  color: #6b7280;
  font-size: 0.75rem;
  text-transform: capitalize;
  line-height: 1.2;
`;

const ChevronIcon = styled.svg`
  width: 16px;
  height: 16px;
  color: #6b7280;
  transition: transform 0.2s;
  
  ${DropdownContainer}:hover & {
    transform: rotate(180deg);
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s;
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #374151;
  font-size: 0.875rem;
  transition: background-color 0.2s;
  
  &:first-child {
    border-radius: 12px 12px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 12px 12px;
  }
  
  &:hover {
    background: #f9fafb;
  }
  
  &.logout {
    color: #ef4444;
    border-top: 1px solid #f3f4f6;
    
    &:hover {
      background: #fef2f2;
    }
  }
`;

const MenuIcon = styled.svg`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({ onOpenSettings }) => {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const getInitials = (firstName?: string, lastName?: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const handleLogout = async () => {
    await logout();
    setIsOpen(false);
  };

  const handleSettings = () => {
    onOpenSettings();
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <DropdownContainer ref={dropdownRef}>
      <UserChip onClick={() => setIsOpen(!isOpen)}>
        <Avatar>
          {getInitials(user?.firstName, user?.lastName)}
        </Avatar>
        <UserInfo>
          <UserName>
            {user?.firstName} {user?.lastName}
          </UserName>
          <UserRole>{user?.role}</UserRole>
        </UserInfo>
        <ChevronIcon viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </ChevronIcon>
      </UserChip>
      
      <DropdownMenu $isOpen={isOpen}>
        <MenuItem onClick={handleSettings}>
          <MenuIcon viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
          </MenuIcon>
          Impostazioni
        </MenuItem>
        <MenuItem onClick={handleLogout} className="logout">
          <MenuIcon viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
          </MenuIcon>
          Esci
        </MenuItem>
      </DropdownMenu>
    </DropdownContainer>
  );
};

export default UserProfileDropdown;